<template>
    <div class="B2BService container my-20 flex flex-col items-center lg:mt-40">
        <CHeading tag="h2" variant="fat-italic">Neváhajte a vyberte si z našich služieb</CHeading>

        <CText class="mb-12 mt-6 max-w-[531px] text-center" variant="muted">
            {{ content.description }}
            <a v-if="content.link" class="text-primary-910 dark:text-primary-300 hover:underline" href="#podmienky-spoluprace">
                Zistiť viac o podmienkach.
            </a>
        </CText>

        <div class="flex w-full flex-col">
            <div
                class="flex w-full flex-row justify-evenly border-b border-solid border-gray-200 dark:border-default text-sm text-gray-500 dark:text-txt-body md:text-base"
            >
                <div
                    v-for="tabName in content.tabs"
                    :key="tabName"
                    class="flex cursor-pointer flex-row items-center justify-center border-b-2 border-transparent py-4 text-center hover:text-primary-910 hover:dark:text-primary-300 sm:w-80"
                    :class="{
                        'border-solid border-primary-910 text-primary-910 dark:text-primary-300': currentTab === tabName,
                        'w-28': content.tabs.length > 1,
                        'w-full': content.tabs.length === 1
                    }"
                    @click="currentTab = tabName"
                >
                    {{ tabs[tabName].title }}
                </div>
            </div>
        </div>

        <div
            class="mb-14 flex w-full flex-col items-center justify-center border-solid border-gray-200 dark:border-default py-10 lg:flex-row lg:border-b"
        >
            <B2BServicesCard v-for="(data, idx) in currentTabItems" :key="idx" :data="data" />
        </div>

        <CButton class="h-14 w-full max-w-[248px]" :href="content.anchor" variant="primary">
            Mám záujem o spoluprácu
        </CButton>
    </div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'

import { B2BType } from '#shared/types/B2B'
import B2BServicesCard from '@/components/b2b/B2BServicesCard.vue'
import CButton from '@/components/ui/CButton.vue'
import CHeading from '@/components/ui/CHeading.vue'
import CText from '@/components/ui/CText.vue'
import { ref, computed, defineProps } from 'vue'

const props = defineProps({
    type: {
        type: String as PropType<B2BType>,
        default: B2BType.DEFAULT
    }
})

const TABS = {
    'inovativne-a-rastuce-firmy': {
        title: 'Inovatívne a rastúce firmy',
        items: [
            {
                title: 'Zamestnanec / Stážista',
                message:
                    'Talenty pre vašu firmu nielen zo Slovenska ale aj zo zahraničia. Prístup k talentovaným Slovákom v zahraničí.',
                img: ['bloomreach'],
                variant: 'img',
                haveBorder: true
            },
            {
                title: 'Zdieľanie vášho know-how',
                message:
                    'Možnosť prepojiť sa na mladých talentovaných ľudí s cieľom spoločného zdieľania know-how na vami vybranú tému.',
                img: ['sudolabs'],
                variant: 'img',
                haveBorder: true
            },
            {
                title: 'Expertná skupina',
                message: 'Získanie názoru a expertízy od skupiny odborníkov na vami vybranú tému/cieľ.',
                img: 'maria-bielikova',
                variant: 'person',
                person: { name: 'Mária Bieliková', job: 'Product Manager' },
                haveBorder: true
            },
            {
                title: 'Mentoring',
                message:
                    'Prepojenie na experta z konkrétnej oblasti, ktorý pomôže posunúť vás ako firmu alebo vašich zamestnancov.',
                img: ['tatra-banka', 'ibm'],
                variant: 'img',
                haveBorder: false
            }
        ]
    },
    'statna-a-verejna-sprava': {
        title: 'Verejný sektor',
        items: [
            {
                title: 'Zamestnanec / Stážista',
                message:
                    'Talenty pre vašu firmu nielen zo Slovenska ale aj zo zahraničia. Prístup k talentovaným Slovákom v zahraničí.',
                img: ['plan-obnovy', 'miiri'],
                variant: 'img',
                haveBorder: true
            },
            {
                title: 'Zdieľanie vášho know-how',
                message:
                    'Možnosť prepojiť sa na mladých talentovaných ľudí s cieľom spoločného zdieľania know-how na vami vybranú tému.',
                img: ['mhsr'],
                variant: 'img',
                haveBorder: true
            },
            {
                title: 'Expertná skupina',
                message: 'Získanie názoru a expertízy od skupiny odborníkov na vami vybranú tému/cieľ.',
                img: 'maria-bielikova',
                variant: 'person',
                person: { name: 'Mária Bieliková', job: 'Product Manager' },
                haveBorder: true
            },
            {
                title: 'Mentoring',
                message:
                    'Prepojenie na experta z konkrétnej oblasti, ktorý pomôže posunúť vás ako firmu alebo vašich zamestnancov.',
                img: ['uhp', 'miiri'],
                variant: 'img',
                haveBorder: false
            }
        ]
    },
    'neziskovky-a-obcianske-iniciativy': {
        title: 'Neziskovky a občianske iniciatívy',
        items: [
            {
                title: 'Dobrovoľník / Zamestnanec / Stážista',
                message:
                    'Talenty pre vašu firmu nielen zo Slovenska ale aj zo zahraničia. Prístup k talentovaným Slovákom v zahraničí.',
                img: ['nexteria', 'tis'],
                variant: 'img',
                haveBorder: true
            },
            {
                title: 'Zdieľanie vášho know-how',
                message:
                    'Možnosť prepojiť sa na mladých talentovaných ľudí s cieľom spoločného zdieľania know-how na vami vybranú tému.',
                img: 'maria-bielikova',
                variant: 'person',
                person: { name: 'Mária Bieliková', job: 'Product Manager' },
                haveBorder: true
            },
            {
                title: 'Expertná skupina',
                message: 'Získanie názoru a expertízy od skupiny odborníkov na vami vybranú tému/cieľ.',
                img: 'maria-bielikova',
                variant: 'person',
                person: { name: 'Mária Bieliková', job: 'Product Manager' },
                haveBorder: true
            },
            {
                title: 'Mentoring',
                message:
                    'Prepojenie na experta z konkrétnej oblasti, ktorý pomôže posunúť vás ako firmu alebo vašich zamestnancov.',
                img: 'maria-bielikova',
                variant: 'person',
                person: { name: 'Mária Bieliková', job: 'Product Manager' },
                haveBorder: false
            }
        ]
    }
} as const

const CONTENT = {
    [B2BType.DEFAULT]: {
        description:
            'Nezáleží, či ste firma, verejný sektor alebo neziskovka, na GROWNi máme príležitosti pre všetkých.',
        link: true,
        tabs: ['inovativne-a-rastuce-firmy', 'statna-a-verejna-sprava', 'neziskovky-a-obcianske-iniciativy'],
        anchor: '#formular'
    },

    [B2BType.GOV]: {
        description:
            'Na GROWNi máme pripravené príležitosti pre vás, ale radi s vami vytvoríme čokoľvek, čo pomôže rozvoju Slovenska.',
        link: false,
        tabs: ['statna-a-verejna-sprava'],
        anchor: '#ponuka-spoluprace'
    },

    [B2BType.NGO]: {
        description: '',
        link: false,
        tabs: [],
        anchor: ''
    }
} as const

const content = computed(() => CONTENT[props.type])
const tabs = TABS
const currentTab = ref(CONTENT[props.type].tabs[0] || '')

const currentTabItems = computed(() => {
    if (!currentTab.value || !(currentTab.value in tabs)) {
        return []
    }
    return tabs[currentTab.value as keyof typeof tabs].items || []
})
</script>
